/**
 * Formik-bound QueryBuilder wrapper for fund-account rules.
 * Sibling of `GuardrailConditionsQueryBuilderFormik` — same skeleton (read `values[fieldId]`, gate
 * on `submitCount`, compute prereqs, call the shared registry hook, render the pure QB)
 * parameterised over the `fund_account_rule` rule_set_type.
 *
 * Unlike the guardrail/comms wrappers, the event is not the author's to pick: this rule set
 * declares a single event server-side, so the key is bound here and the prerequisite gate is
 * LOB + sub-org only. The catalog's exclusion of `fund_account_number` is applied by the BE
 * (`excluded_event_fields`), so nothing about it appears on this side.
 */

import React, { useEffect, useRef } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { getIn, useFormikContext } from 'formik';

import useOrganization from '../../../../OrganizationContext';
import RuleConditionsQueryBuilder from '../../Conditions/QueryBuilder/RuleConditionsQueryBuilder';
import { useRuleSetConditionFieldRegistry } from '../../Conditions/QueryBuilder/useRuleSetConditionFieldRegistry';

const FUND_ACCOUNT_RULE_TYPE = 'fund_account_rule';
/** The single event this rule set dispatches on — `rule_set_configs.FUND_ACCOUNT_RULE_EVENT_KEY`.
 *  Forwarded so the BE narrows the catalog by the event's scope, exactly as for the siblings. */
const FUND_ACCOUNT_RULE_EVENT_KEY = 'payment_made_event';

interface FundAccountConditionsQueryBuilderFormikProps {
  /** Formik path holding the `conditions_json` tree (default: `'conditions_json'`). */
  fieldId?: string;
  /** Every LOB the rule is scoped to. Forwarded whole: the catalog is the cross-LOB intersection,
   *  and a rule offered one LOB's fields can be saved with conditions the other LOBs' payments
   *  never carry. Empty means "not narrowed yet" — including the All-LOBs pick, which the dialog
   *  strips because its sentinel names no LOB. */
  lobs: string[];
  isAllSubOrgs: boolean;
  subOrgIds: number[];
  /** Surfaces the catalog-fetch loading state so the dialog can disable Save while fields are in
   *  flight. Idle (false) while prerequisites aren't met. */
  onLoadingChange?: (isLoading: boolean) => void;
}

interface PrerequisiteCheck {
  prerequisitesMet: boolean;
  prerequisiteMessage: string | null;
}

/** A non-empty LOB scope + (sub-org pick OR all-sub-orgs OR sub-orgs disabled). The event is fixed,
 *  so it is not part of the gate — the only difference from the guardrail sibling's predicate. */
const checkPrerequisites = (
  lobs: string[],
  isAllSubOrgs: boolean,
  subOrgIds: number[],
  subOrgsEnabled: boolean
): PrerequisiteCheck => {
  const hasOrgScope = !subOrgsEnabled || isAllSubOrgs || (subOrgIds && subOrgIds.length > 0);
  if (lobs.length > 0 && hasOrgScope) {
    return { prerequisitesMet: true, prerequisiteMessage: null };
  }
  const subOrgPart = subOrgsEnabled ? ' and a Sub-Organization' : '';
  return {
    prerequisitesMet: false,
    prerequisiteMessage: `Select a Line of Business${subOrgPart} to configure conditions.`,
  };
};

const FundAccountConditionsQueryBuilderFormik: React.FC<FundAccountConditionsQueryBuilderFormikProps> = ({
  fieldId = 'conditions_json',
  lobs,
  isAllSubOrgs,
  subOrgIds,
  onLoadingChange,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const value = (getIn(values, fieldId) as RuleGroupType | null | undefined) ?? null;

  // `useOrganization` is exported from a JSX file without types; cast at the boundary.
  const { organizationId, subOrganizationEnabled } = useOrganization() as {
    organizationId: number;
    subOrganizationEnabled?: boolean;
  };

  const { prerequisitesMet, prerequisiteMessage } = checkPrerequisites(
    lobs,
    isAllSubOrgs,
    subOrgIds,
    !!subOrganizationEnabled
  );

  const { fields, isLoading } = useRuleSetConditionFieldRegistry({
    organizationId,
    enabled: prerequisitesMet,
    ruleSetType: FUND_ACCOUNT_RULE_TYPE,
    eventKey: FUND_ACCOUNT_RULE_EVENT_KEY,
    lobs,
    isAllSubOrgs,
    subOrgIds,
  });

  // Fire only when the loading boolean flips, never on the callback's identity — an inline arrow
  // from a caller would otherwise re-run this every render. Holding the callback in a ref keeps
  // the latest one reachable without putting it in the dependency list, so no lint suppression is
  // needed here.
  const onLoadingChangeRef = useRef(onLoadingChange);
  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);

  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);

  return (
    <RuleConditionsQueryBuilder
      value={value}
      onChange={(q) => setFieldValue(fieldId, q)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={prerequisiteMessage}
      emptyMessage="No condition fields available for this line of business."
      // The rule set dispatches on its one fixed event, so a rule always has an event time.
      hasTriggeringEvent
    />
  );
};

export default FundAccountConditionsQueryBuilderFormik;
